/**
 * KanbanCategoryTabs - Tabs de categorias do Kanban
 * 
 * Permite navegar entre diferentes categorias e criar/editar/excluir categorias
 * Categorias com is_default=true não podem ser excluídas (proteção via trigger no DB)
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-28 v2.2.0 - Abas reordenáveis por drag and drop (dnd-kit),
 *            com alça de 6 pontinhos (mesmo padrão das abas de pipelines do CRM).
 *            A nova ordem é persistida via prop `onReorderCategories`.
 */

import { useState, useEffect } from 'react';
import { Plus, MoreHorizontal, Pencil, Trash2, Check, X, Lock, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollableTabsBar } from '@/components/ui/scrollable-tabs-bar';
// Drag and drop das abas (mesmo padrão do DealPipelineTabs)
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';


import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { KanbanCategory } from '@/hooks/useKanban';

// Cores pré-definidas para categorias
const PRESET_COLORS = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#f97316', // orange
  '#6366f1', // indigo
];

interface KanbanCategoryTabsProps {
  categories: KanbanCategory[];
  selectedCategoryId: string | null;
  onSelectCategory: (id: string) => void;
  onCreateCategory: (name: string, color: string) => Promise<any>;
  onUpdateCategory: (id: string, updates: Partial<KanbanCategory>) => Promise<any>;
  onDeleteCategory: (id: string) => Promise<boolean>;
  // v2.1.0: Permissões granulares (menu 'kanban') para ocultar botões
  canCreateCategory?: boolean;
  canEditCategory?: boolean;
  canDeleteCategory?: boolean;
  // v2.2.0: callback de reordenação das abas. Ausente => drag desabilitado.
  onReorderCategories?: (categoryIds: string[]) => Promise<any> | void;
}

/**
 * Aba individual sortable (v2.2.0)
 * O arraste é iniciado pela alça de 6 pontinhos (GripVertical), mantendo o
 * clique na aba exclusivo para selecionar a categoria.
 */
function SortableCategoryTab({
  category,
  children,
  disabled,
}: {
  category: KanbanCategory;
  children: React.ReactNode;
  disabled: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : undefined,
  } as React.CSSProperties;

  return (
    <div ref={setNodeRef} style={style} className="relative group" {...attributes}>
      {/* Alça de arraste (mesmo padrão visual das colunas, cards e pipelines) */}
      {!disabled && (
        <button
          type="button"
          aria-label="Arraste para reordenar a categoria"
          title="Arraste para reordenar"
          className={cn(
            'absolute left-1 top-1/2 -translate-y-1/2 z-10',
            'text-muted-foreground/40 hover:text-foreground',
            'cursor-grab active:cursor-grabbing transition-colors'
          )}
          onClick={(e) => e.stopPropagation()}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      )}
      {children}
    </div>
  );
}

export function KanbanCategoryTabs({
  categories,
  selectedCategoryId,
  onSelectCategory,
  onCreateCategory,
  onUpdateCategory,
  onDeleteCategory,
  // Default true para retrocompatibilidade
  canCreateCategory = true,
  canEditCategory = true,
  canDeleteCategory = true,
  onReorderCategories,
}: KanbanCategoryTabsProps) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<KanbanCategory | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState(PRESET_COLORS[0]);

  // ==========================================================================
  // v2.2.0: reordenação das abas (drag and drop)
  // ==========================================================================
  // Só permite reordenar quem pode editar categorias e quando há callback
  const canReorder = !!onReorderCategories && canEditCategory && categories.length > 1;

  // Ordem local (feedback otimista durante o arraste)
  const [orderedCategories, setOrderedCategories] = useState<KanbanCategory[]>(categories);

  useEffect(() => {
    setOrderedCategories(categories);
  }, [categories]);

  // Clique só vira arraste após 6px de deslocamento
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Aplica a nova ordem e persiste (rollback em caso de erro)
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorderCategories) return;

    const oldIndex = orderedCategories.findIndex((c) => c.id === active.id);
    const newIndex = orderedCategories.findIndex((c) => c.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const previous = orderedCategories;
    const nova = arrayMove(orderedCategories, oldIndex, newIndex);
    setOrderedCategories(nova);

    try {
      await onReorderCategories(nova.map((c) => c.id));
    } catch {
      // Reverte em caso de falha (toast de erro vem do hook)
      setOrderedCategories(previous);
    }
  };


  // Abre dialog de edição
  const handleOpenEdit = (category: KanbanCategory) => {
    setEditingCategory(category);
    setNewCategoryName(category.name);
    setNewCategoryColor(category.color);
    setShowEditDialog(true);
  };

  // Abre dialog de exclusão
  const handleOpenDelete = (category: KanbanCategory) => {
    setEditingCategory(category);
    setShowDeleteDialog(true);
  };

  // Cria categoria — só fecha o diálogo em caso de sucesso
  const handleCreate = async () => {
    if (!newCategoryName.trim()) return;
    // Proteção defensiva: backend já bloqueia, mas evita chamada e erro silencioso
    if (!canCreateCategory) {
      toast.error('Você não tem permissão para criar categorias do Kanban.');
      return;
    }
    const result = await onCreateCategory(newCategoryName.trim(), newCategoryColor);
    if (result) {
      setNewCategoryName('');
      setNewCategoryColor(PRESET_COLORS[0]);
      setShowCreateDialog(false);
    }
  };

  // Atualiza categoria — só fecha em caso de sucesso
  const handleUpdate = async () => {
    if (!editingCategory || !newCategoryName.trim()) return;
    if (!canEditCategory) {
      toast.error('Você não tem permissão para editar categorias do Kanban.');
      return;
    }
    const result = await onUpdateCategory(editingCategory.id, {
      name: newCategoryName.trim(),
      color: newCategoryColor,
    });
    if (result) {
      setShowEditDialog(false);
      setEditingCategory(null);
    }
  };

  // Exclui categoria — só fecha em caso de sucesso
  const handleDelete = async () => {
    if (!editingCategory) return;
    if (!canDeleteCategory) {
      toast.error('Você não tem permissão para excluir categorias do Kanban.');
      return;
    }
    const ok = await onDeleteCategory(editingCategory.id);
    if (ok) {
      setShowDeleteDialog(false);
      setEditingCategory(null);
    }
  };

  return (
    <div className="flex items-center gap-2 min-w-0">
      {/* Faixa rolável: com muitas categorias o usuário navega por barra/setas/touchpad */}
      <ScrollableTabsBar activeKey={selectedCategoryId} className="min-w-0 flex-1">
        <Tabs value={selectedCategoryId || ''} onValueChange={onSelectCategory}>
          <TabsList className="h-9 w-max">
            {/* v2.2.0: abas reordenáveis por drag and drop (alça de 6 pontinhos) */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
            <SortableContext
              items={orderedCategories.map((c) => c.id)}
              strategy={horizontalListSortingStrategy}
            >
            {orderedCategories.map((category) => (
            <SortableCategoryTab key={category.id} category={category} disabled={!canReorder}>



              <TabsTrigger
                value={category.id}
                className={cn(
                  'px-4',
                  (canEditCategory || canDeleteCategory) && 'pr-8',
                  canReorder && 'pl-6' // espaço para a alça de arraste
                )}
                style={{ 
                  borderBottomColor: selectedCategoryId === category.id ? category.color : 'transparent',
                  borderBottomWidth: 2,
                }}
              >

                <div 
                  className="w-2 h-2 rounded-full mr-2"
                  style={{ backgroundColor: category.color }}
                />
                {category.name}
              </TabsTrigger>
              
              {/* Menu de opções — só renderiza se houver alguma ação disponível */}
              {(canEditCategory || canDeleteCategory) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        'absolute right-0 top-1/2 -translate-y-1/2 h-6 w-6',
                        'opacity-0 group-hover:opacity-100 transition-opacity'
                      )}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreHorizontal className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {canEditCategory && (
                      <DropdownMenuItem onClick={() => handleOpenEdit(category)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Editar
                      </DropdownMenuItem>
                    )}
                    {canDeleteCategory && !category.is_default && (
                      <DropdownMenuItem 
                        onClick={() => handleOpenDelete(category)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Excluir
                      </DropdownMenuItem>
                    )}
                    {canDeleteCategory && category.is_default && (
                      <DropdownMenuItem disabled className="text-muted-foreground">
                        <Lock className="h-4 w-4 mr-2" />
                        Categoria protegida
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </SortableCategoryTab>
            ))}
            </SortableContext>
            </DndContext>
          </TabsList>
        </Tabs>
      </ScrollableTabsBar>




      {/* Botão Nova Categoria — somente para quem tem permissão */}
      {canCreateCategory && (
        <Button
          variant="outline"
          size="sm"
          className="h-9"
          onClick={() => setShowCreateDialog(true)}
        >
          <Plus className="h-4 w-4 mr-1" />
          Nova Categoria
        </Button>
      )}

      {/* Dialog Criar Categoria */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Categoria</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input
                placeholder="Ex: Vendas, Suporte..."
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              />
            </div>
            <div className="space-y-2">
              <Label>Cor</Label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={cn(
                      'w-8 h-8 rounded-full transition-all',
                      newCategoryColor === color && 'ring-2 ring-offset-2 ring-primary'
                    )}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewCategoryColor(color)}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={!newCategoryName.trim()}>
              Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Editar Categoria */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Categoria</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleUpdate()}
              />
            </div>
            <div className="space-y-2">
              <Label>Cor</Label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={cn(
                      'w-8 h-8 rounded-full transition-all',
                      newCategoryColor === color && 'ring-2 ring-offset-2 ring-primary'
                    )}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewCategoryColor(color)}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdate} disabled={!newCategoryName.trim()}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmar Exclusão */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Excluir categoria "{editingCategory?.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Todas as fases desta categoria serão excluídas e as conversas serão desvinculadas.
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
